/**
 * PlatformReadyScreen - Final onboarding screen showing comprehensive platform statistics
 * Displays real data from all connected sources without progress circles or action buttons
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useDataPreview } from '../../hooks/useDataPreview';

const PlatformReadyScreen: React.FC = () => {
  const navigate = useNavigate();
  const { 
    documentStats, 
    marketStats, 
    setMarketStats,
    allUploads, 
    connectedSources 
  } = useOnboarding();
  
  const { dataPreview, fetchDataPreview } = useDataPreview();

  // Fetch email data preview when email is connected
  useEffect(() => {
    if (connectedSources.includes('email') && !dataPreview) {
      console.log('Fetching data preview for connected email...');
      fetchDataPreview().catch(error => {
        console.error('Failed to fetch data preview:', error);
      });
    }
  }, [connectedSources, dataPreview, fetchDataPreview]);

  // Update market stats when market data is connected
  useEffect(() => {
    if (connectedSources.includes('market') && !marketStats.isConnected) {
      console.log('Market data detected as connected, updating market stats...');
      setMarketStats({
        isConnected: true,
        lastQuoteTime: new Date().toISOString(),
        quotesThisHour: 0, // Will be updated by polling
        rateLimitRemaining: 60 // Default value
      });
    }
  }, [connectedSources, marketStats.isConnected, setMarketStats]);

  // Helper function to check if a file is a PDF
  const isPDFFile = (filename: string): boolean => {
    return filename.toLowerCase().endsWith('.pdf');
  };

  // Calculate statistics
  const excelFiles = allUploads.filter(upload => 
    !isPDFFile(upload.filename) && ['ready', 'ingesting', 'complete'].includes(upload.upload_status)
  );
  
  const pdfFiles = allUploads.filter(upload => 
    isPDFFile(upload.filename) && ['ready', 'ingesting', 'complete'].includes(upload.upload_status)
  );

  const totalDataPoints = allUploads
    .filter(upload => ['ready', 'ingesting', 'complete'].includes(upload.upload_status))
    .reduce((total, upload) => total + (upload.total_rows || 0), 0);

  return (
    <div className="min-h-screen bg-kielis-glacier py-16">
      <div className="container mx-auto px-5">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-kielis-navy mb-4">Your Kielis Platform is Ready!</h2>
          <p className="text-gray-600 text-lg">
            Here's a comprehensive overview of your imported data and system status.
          </p>
        </div>

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {/* Portfolio Data Statistics */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-kielis-navy">📊 Portfolio Data</h4>
              <span className="text-2xl">📊</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Documents</span>
                <span className="font-semibold text-kielis-navy">{documentStats.totalDocuments}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Approved Documents</span>
                <span className="font-semibold text-green-600">{documentStats.approvedDocuments}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Rows Imported</span>
                <span className="font-semibold text-kielis-navy">{documentStats.totalRows.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Data Quality Score</span>
                <span className={`font-semibold ${
                  documentStats.qualityScore >= 90 ? 'text-green-600' : 
                  documentStats.qualityScore >= 70 ? 'text-yellow-600' : 'text-red-600'
                }`}>
                  {documentStats.qualityScore.toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Avg Processing Time</span>
                <span className="font-semibold text-gray-700">{documentStats.avgProcessingTime.toFixed(1)}s</span>
              </div>
            </div>
          </div>

          {/* Document Analysis Statistics */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-kielis-navy">📄 Document Analysis</h4>
              <span className="text-2xl">📄</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Excel/CSV Files</span>
                <span className="font-semibold text-kielis-navy">{excelFiles.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">PDF Documents</span>
                <span className="font-semibold text-kielis-navy">{pdfFiles.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Data Points</span>
                <span className="font-semibold text-kielis-navy">{totalDataPoints.toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Pending Approval</span>
                <span className={`font-semibold ${
                  documentStats.pendingApproval > 0 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  {documentStats.pendingApproval}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status</span>
                <span className="font-semibold text-green-600">✓ Active</span>
              </div>
            </div>
          </div>

          {/* Communication Statistics */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-kielis-navy">📧 Communication</h4>
              <span className="text-2xl">📧</span>
            </div>
            <div className="space-y-3">
              {connectedSources.includes('email') && dataPreview ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Email Threads</span>
                    <span className="font-semibold text-kielis-navy">
                      {dataPreview.summary.total_emails.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Unread Emails</span>
                    <span className={`font-semibold ${
                      dataPreview.summary.unread_emails > 0 ? 'text-yellow-600' : 'text-green-600'
                    }`}>
                      {dataPreview.summary.unread_emails.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Calendar Events</span>
                    <span className="font-semibold text-kielis-navy">
                      {dataPreview.summary.total_events.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Upcoming Meetings</span>
                    <span className="font-semibold text-blue-600">
                      {dataPreview.summary.upcoming_events.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status</span>
                    <span className="font-semibold text-green-600">✓ Synced</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Email Threads</span>
                    <span className="font-semibold text-gray-400">Not connected</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Calendar Events</span>
                    <span className="font-semibold text-gray-400">Not connected</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Meeting History</span>
                    <span className="font-semibold text-gray-400">Not connected</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status</span>
                    <span className="font-semibold text-gray-400">—</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Market Data Statistics */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-kielis-navy">📈 Market Data</h4>
              <span className="text-2xl">📈</span>
            </div>
            <div className="space-y-3">
              {connectedSources.includes('market') && marketStats.isConnected ? (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Finnhub API</span>
                    <span className="font-semibold text-green-600">✓ Connected</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Quotes This Hour</span>
                    <span className="font-semibold text-kielis-navy">{marketStats.quotesThisHour}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Rate Limit</span>
                    <span className={`font-semibold ${
                      marketStats.rateLimitRemaining > 30 ? 'text-green-600' : 
                      marketStats.rateLimitRemaining > 10 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {marketStats.rateLimitRemaining}/min
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Last Quote</span>
                    <span className="font-semibold text-gray-700">
                      {marketStats.lastQuoteTime 
                        ? new Date(marketStats.lastQuoteTime).toLocaleTimeString() 
                        : 'None yet'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status</span>
                    <span className="font-semibold text-green-600">✓ Live</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Finnhub API</span>
                    <span className="font-semibold text-gray-400">Not connected</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Real-time Quotes</span>
                    <span className="font-semibold text-gray-400">Not available</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Company News</span>
                    <span className="font-semibold text-gray-400">Not available</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Status</span>
                    <span className="font-semibold text-gray-400">—</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* System Overview */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-kielis-navy">⚡ System Overview</h4>
              <span className="text-2xl">⚡</span>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Connected Sources</span>
                <span className="font-semibold text-kielis-navy">{connectedSources.length}/6</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">AI Agents</span>
                <span className="font-semibold text-green-600">✓ Active</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Compliance</span>
                <span className="font-semibold text-green-600">✓ FINSA Ready</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Data Encryption</span>
                <span className="font-semibold text-green-600">✓ AES-256</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Platform Status</span>
                <span className="font-semibold text-green-600">✓ Ready</span>
              </div>
            </div>
          </div>

          {/* Connected Sources Summary */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-medium text-kielis-navy">🔗 Connected Sources</h4>
              <span className="text-2xl">🔗</span>
            </div>
            <div className="space-y-2">
              {connectedSources.length > 0 ? (
                connectedSources.map(sourceId => {
                  const source = ['Excel/CSV', 'Email', 'PDF', 'Market Data', 'CRM'][
                    ['excel', 'email', 'pdf', 'market', 'crm'].indexOf(sourceId)
                  ];
                  const icon = ['📊', '📧', '📄', '📈', '👥'][
                    ['excel', 'email', 'pdf', 'market', 'crm'].indexOf(sourceId)
                  ];
                  return (
                    <div key={sourceId} className="flex items-center gap-2 p-2 bg-green-50 rounded">
                      <span className="text-lg">{icon}</span>
                      <span className="font-medium text-green-700">{source}</span>
                      <span className="ml-auto text-green-600">✓</span>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-500 text-center py-4">No sources connected yet</p>
              )}
            </div>
          </div>
        </div>

        <div className="text-center">
          <button 
            className="bg-kielis-navy text-white px-8 py-4 rounded-lg text-xl font-medium hover:bg-opacity-90 transition-all"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
          <p className="text-gray-600 text-sm mt-4">
            All systems are operational. You can now start using Kielis for client meetings.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlatformReadyScreen;